"use client";

import { useState } from "react";
import { useJsonResource } from "@/hooks/useJsonResource";
import { requestJson } from "@/lib/clientRequest";

type Op = { kind: "same" | "removed" | "added"; text: string };
type Change = {
  id: string;
  workerName: string;
  requestedChange: string;
  wholeRewrite: boolean;
  status: "pending" | "applied" | "discarded" | "stale" | "replaced";
  diff: Op[];
};

/** Unchanged lines shown around each change; the rest fold into a count. */
const CONTEXT = 1;

function hunks(diff: Op[]): (Op | { kind: "fold"; count: number })[] {
  const near = diff.map((op, i) => op.kind !== "same" || diff.slice(Math.max(0, i - CONTEXT), i + CONTEXT + 1).some((o) => o.kind !== "same"));
  const out: (Op | { kind: "fold"; count: number })[] = [];
  for (let i = 0; i < diff.length; i++) {
    if (near[i]) { out.push(diff[i]); continue; }
    const last = out[out.length - 1];
    if (last && last.kind === "fold") last.count++;
    else out.push({ kind: "fold", count: 1 });
  }
  return out;
}

const DONE: Record<Exclude<Change["status"], "pending">, string> = {
  applied: "Applied. The plan is being rebuilt from the new text; the approved plan keeps running until you approve the new one.",
  discarded: "Discarded. Nothing was changed.",
  stale: "Not applied: the instructions changed after this was proposed. Ask Smithers to propose it again.",
  replaced: "Replaced by a newer proposed change.",
};

/**
 * A change Smithers proposed to a worker's instructions that removes or
 * rewrites text. Nothing is written until the person applies it here.
 */
export default function ChatInstructionChangeControl({ workerId, changeId }: { workerId: string; changeId: string }) {
  const url = `/api/workers/${encodeURIComponent(workerId)}/instruction-changes/${encodeURIComponent(changeId)}`;
  const { data, error: loadError, refresh } = useJsonResource<Change>(url);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function decide(action: "apply" | "discard") {
    setBusy(true);
    setError("");
    try {
      await requestJson(url, { method: "POST", body: { action }, fallback: "Could not save your decision" });
      window.dispatchEvent(new Event("caw:activity-refresh"));
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Could not save your decision"); }
    finally { await refresh(); setBusy(false); }
  }
  if (!data) return <span className="block text-xs text-zinc-400">{loadError ?? "Loading the proposed change"}</span>;
  const removed = data.diff.filter((op) => op.kind === "removed" && op.text.trim()).length;
  const added = data.diff.filter((op) => op.kind === "added" && op.text.trim()).length;
  return (
    <span role="group" aria-label={`Proposed change to ${data.workerName}`} className="my-2 block rounded-xl border border-attention/60 bg-attention/8 p-3 text-[13px] not-italic">
      <span className="block text-[11px] font-medium tracking-wide text-attention uppercase">Needs your approval</span>
      <span className="mt-0.5 block font-semibold text-zinc-100">Change to {data.workerName}&apos;s instructions</span>
      <span className="mt-0.5 block text-zinc-300">{data.requestedChange}</span>
      <span className="mt-1 block text-xs text-zinc-400">
        {data.wholeRewrite ? "Whole-text rewrite · " : ""}{removed} line{removed === 1 ? "" : "s"} removed or rewritten · {added} added
      </span>
      <span className="mt-2 block max-h-80 overflow-auto rounded-lg border border-zinc-800 bg-zinc-950/70 py-1 font-mono text-[12px] leading-relaxed">
        {hunks(data.diff).map((op, i) =>
          op.kind === "fold" ? (
            <span key={i} className="block px-2 text-zinc-500">⋯ {op.count} unchanged line{op.count === 1 ? "" : "s"}</span>
          ) : (
            <span key={i} className={`block px-2 whitespace-pre-wrap ${op.kind === "removed" ? "bg-danger/12 text-danger line-through decoration-danger/60" : op.kind === "added" ? "bg-success/12 text-success" : "text-zinc-400"}`}>
              <span aria-hidden="true" className="mr-1.5 select-none">{op.kind === "removed" ? "−" : op.kind === "added" ? "+" : " "}</span>
              <span className="sr-only">{op.kind === "removed" ? "Removed: " : op.kind === "added" ? "Added: " : ""}</span>
              {op.text || " "}
            </span>
          )
        )}
      </span>
      {data.status === "pending" ? (
        <span className="mt-2.5 flex gap-2">
          <button type="button" disabled={busy} onClick={() => void decide("apply")} className="rounded-lg bg-violet-600 px-3 py-1.5 font-medium text-white disabled:opacity-50">Apply change</button>
          <button type="button" disabled={busy} onClick={() => void decide("discard")} className="rounded-lg border border-zinc-600 px-3 py-1.5 text-zinc-200 disabled:opacity-50">Discard</button>
        </span>
      ) : (
        <span role="status" className={`mt-2 block text-xs ${data.status === "applied" ? "text-success" : data.status === "stale" ? "text-danger" : "text-zinc-400"}`}>{DONE[data.status]}</span>
      )}
      {error && <span role="alert" className="mt-2 block text-xs text-danger">{error}</span>}
    </span>
  );
}
